import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  InputBase,
  Paper,
  Typography,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import React, { useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SearchSharpIcon from "@mui/icons-material/SearchSharp";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import axios from "axios";
import VideoCard from "../components/VideoCard";

/**
 * Step 1: Video Selection
 * Search and select multiple videos for MRSS publishing
 */
function VideoSelection({
  selectedVideos = [],
  handleVideoSelect,
  handleAddVideo,
  handleRemoveVideo,
}) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiVideoCheck, setAiVideoCheck] = useState(false);
  const searchRef = useRef(null);

  const fetchVideos = async (searchText) => {
    try {
      setLoading(true);
      const keys = searchText
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean);

      const requests = keys.map((key) => {
        const params = {
          search: key,
          limit: 20,
          orderType: "DES",
          page: 1,
        };
        if (aiVideoCheck) {
          params.category_27 = "true";
          params.ai_based = "true";
        }
        return axios.get(
          process.env.REACT_APP_WEBSITE + "/apis/videos-filter",
          { params },
        );
      });

      const responses = await Promise.all(requests);
      const allVideos = responses.flatMap((r) => r.data);
      const unique = Array.from(
        new Map(allVideos.map((v) => [v.unique_key, v])).values(),
      );
      setVideos(unique);
      searchRef.current.value = "";
    } catch (error) {
      console.error("Error fetching videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const searchText = searchRef.current.value;
    if (searchText.trim()) {
      fetchVideos(searchText);
    }
  };

  const handleAdd = (video) => () => {
    handleAddVideo(video);
    setVideos((prev) => prev.filter((v) => v.unique_key !== video.unique_key));
  };

  const handleRemove = (video) => () => {
    handleRemoveVideo(video);
  };

  return (
    <Box>
      {/* Search Section */}
      <Box mb={3}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <SearchSharpIcon fontSize="large" />
            <Typography variant="h6">Search Videos</Typography>
          </Box>
          <Tooltip title="Enable to fetch AI-generated videos only" arrow>
            <FormControlLabel
              control={
                <Switch
                  checked={aiVideoCheck}
                  onChange={(e) => setAiVideoCheck(e.target.checked)}
                  color="primary"
                />
              }
              label="AI Videos"
            />
          </Tooltip>
        </Box>

        <Paper
          component={InputBase}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch();
            }
          }}
          inputRef={searchRef}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  searchRef.current.value = "";
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={handleSearch}>
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          }
          placeholder="Search by unique key (comma-separated for multiple)"
          sx={{
            width: "100%",
            maxWidth: 800,
            height: 50,
            padding: 2,
            borderRadius: 4,
            bgcolor: "transparent",
          }}
        />
      </Box>
      {/* Selected Videos Summary */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <VideoLibraryIcon color="primary" />
          <Typography variant="h6">Selected Videos</Typography>
          <Chip label={selectedVideos.length} color="primary" size="small" />
        </Box>

        {selectedVideos.length > 0 || videos.length > 0 ? (
          <Grid container spacing={2}>
            {selectedVideos.map((video) => (
              <Grid item xs={12} sm={6} md={4} key={video.unique_key}>
                <VideoCard
                  video={video}
                  _component={Paper}
                  onDelete={handleRemove(video)}
                  sx={{ bgcolor: "white", height: "100%" }}
                  selected
                />
              </Grid>
            ))}
            {/* Search Results */}
              {loading ? (
                <Grid item xs={12} textAlign="center">
                  <CircularProgress size="large" />
                </Grid>
              ) : videos.length > 0 ? (
                videos.map((video) => (
                  <Grid item xs={12} sm={6} md={4} key={video.unique_key}>
                    <VideoCard
                      video={video}
                      handleAdd={handleAdd}
                      sx={{ height: "100%" }}
                    />
                  </Grid>
                ))
              ) : (
                <Grid item xs={12} textAlign="center">
                  <Box
                    component="img"
                    src="https://www.indiarefurb.com/assets/img/no-result-found.png"
                    alt="No results"
                    sx={{ maxWidth: 400, opacity: 0.6 }}
                  />
                  <Typography variant="body2" color="textSecondary" mt={2}>
                    {videos.length === 0 && !loading
                      ? "Search for videos to add to your feed"
                      : "No videos found"}
                  </Typography>
                </Grid>
              )}
          
          </Grid>
        ) : (
          <Paper
            elevation={1}
            sx={{
              p: 3,
              textAlign: "center",
              bgcolor: "transparent !important",
            }}
          >
            <Typography variant="body2" color="textSecondary">
              No videos selected yet. Search and add videos below.
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
}

export default VideoSelection;
